import { useEffect, useMemo, useState } from 'react';
import type { VoidFunctionComponent } from 'react';

import { OIDCProviderScope } from '../../../../../shared/model-typings/oidcprovider';
import { UserTeamAndRoleManagement } from '../../../../../shared/organizations/types';
import { isFreeTier } from '../../../../../shared/subscriptions';
import { Action, ResourceType } from '../../../../../typings/permissions';
import { useParams } from '../../../../contexts/RouterContext';
import { window } from '../../../../globals';
import { useHasPermissionForTeamMembers, useHasPermissionForTeamProjects } from '../../../../helpers/permissions';
import { useDebouncedInputValue } from '../../../../hooks';
import { useTeam } from '../../../../hooks/api/teams';
import { useHasPermission } from '../../../../shared/permissions';
import { AccessGate } from '../../../generic';
import Loader from '../../../generic/Loader';
import Tabs from '../../../generic/Tabs';
import Header from '../../../shared/settings/SettingsPageHeader';
import SettingsPageTableActions from '../../../shared/settings/SettingsPageTableActions';
import ModalCreateOIDCProvider from '../../integrations/oidcProviders/ModalCreateOIDCProvider';
import OIDCProvidersTable from '../../integrations/oidcProviders/OIDCProvidersTable';
import { useCanManageOIDCProvider, useCanViewOIDCProviderForTeam } from '../../shared/permissions';
import useUserRoleManagement from '../hooks/useUserRoleManagement';
import type { UpdateTeamRequest } from '../../../../../shared/types/routes/teams';

import { EditTeamModal } from './CreateEditTeamModal';
import TeamMembersTable from './management/TeamMembersTable';
import TeamProjects from './TeamProjects';

type TabsAndModals = 'members' | 'projects' | 'releaseGroups' | 'oidcProviders';

/**
 * This is the "management" page for a team, which allows you to manage the team's members and projects.
 */
const TeamSettings: VoidFunctionComponent = () => {
  const params = useParams<{ id: string }>();
  const { data: team, isLoading: isTeamLoading } = useTeam(Number(params.id));

  //  Permissions
  const hasPermission = useHasPermission();
  const hasPermissionForTeamMembers = useHasPermissionForTeamMembers();
  const hasPermissionForTeamProjects = useHasPermissionForTeamProjects();
  const { data: userRoleManagement } = useUserRoleManagement();
  const isTeamRoleIDPManaged = userRoleManagement?.teams === UserTeamAndRoleManagement.IDP;
  const isTeamMembershipIDPManaged = (userRoleManagement?.teams === UserTeamAndRoleManagement.IDP ||
      userRoleManagement?.teams === UserTeamAndRoleManagement.IDP_TEAMS_FOSSA_ROLES);
  const canViewTeamOIDCProviders = useCanViewOIDCProviderForTeam(team?.id ?? 0);
  const canManageTeamOIDCProviders = useCanManageOIDCProvider(team?.id ?? 0);
  const isOrgFreeTier = isFreeTier({
    plan: window.user?.organization?.stripe_plan,
    stripePriceLimit: window.user?.organization?.stripePriceLimit,
  });

  //  OIDC filter
  const oidcFilter = useMemo(() => ({
    scope: OIDCProviderScope.TEAM,
    scopeId: team?.id ?? 0,
  }), [team?.id]);

  //  State
  const {
    value: searchValue,
    onChange: setSearchValue,
    debouncedValue: debouncedSearch,
  } = useDebouncedInputValue();
  const [isEditing, setIsEditing] = useState(false);
  const [activeTab, setActiveTab] = useState<TabsAndModals>('members');
  const [modalToShow, setModalToShow] = useState<TabsAndModals | null>(null);

  // Clear search when switching tabs
  useEffect(() => {
    setSearchValue('');
  }, [activeTab, setSearchValue]);

  //  Mostly for type safety
  if (!team) {
    return null;
  }

  if (isTeamLoading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '300px' }}>
        <Loader />
      </div>
    );
  }

  let searchPlaceholder = 'Search team members';
  if (activeTab === 'projects') {
    searchPlaceholder = 'Search team projects';
  } else if (activeTab === 'releaseGroups') {
    searchPlaceholder = 'Search team release groups';
  } else if (activeTab === 'oidcProviders') {
    searchPlaceholder = 'Search by issuer';
  }

  let canAdd = false;
  if (activeTab === 'members') {
    canAdd = hasPermissionForTeamMembers(Action.Edit, team.id) && !isTeamMembershipIDPManaged;
  } else if (activeTab === 'projects' || activeTab === 'releaseGroups') {
    canAdd = hasPermissionForTeamProjects(Action.Add, team.id);
  } else if (activeTab === 'oidcProviders') {
    canAdd = canManageTeamOIDCProviders && !isOrgFreeTier;
  }

  let buttonLabel = 'Add Members';
  if (activeTab === 'projects' || activeTab === 'releaseGroups') {
    buttonLabel = 'Add Projects or Release Groups';
  } else if (activeTab === 'oidcProviders') {
    buttonLabel = 'Add an OIDC provider';
  }

  const canEditTeam = hasPermission({ resourceType: ResourceType.TeamAny, action: Action.Edit })
    || hasPermission({ resourceType: ResourceType.Team, action: Action.Edit, teamId: team.id });

  return (
    <div className="team-settings">
      <Header heading={team.name}>
        <Header.BackLink to="/account/settings/organization/teams" text="Back to Teams" />
        <Header.TooltipButton
          id="edit-team-group"
          isDisabled={!canEditTeam}
          onClick={() => setIsEditing(true)}
          showTooltip={!canEditTeam}
          tooltip="You do not have permission to edit this team group."
        >
          Edit Team
        </Header.TooltipButton>
      </Header>
      <Tabs>
        <Tabs.Tab
          label="Members"
          isActive={activeTab === 'members'}
          onSelect={() => setActiveTab('members')}
          badgeCount={team.teamMembersCount ?? 0}
        />
        <Tabs.Tab
          label="Projects"
          isActive={activeTab === 'projects'}
          onSelect={() => setActiveTab('projects')}
          badgeCount={team.teamProjectsCount ?? 0}
        />
        <Tabs.Tab
          label="Release Groups"
          isActive={activeTab === 'releaseGroups'}
          onSelect={() => setActiveTab('releaseGroups')}
          badgeCount={team.teamReleaseGroupsCount ?? 0}
        />
        {canViewTeamOIDCProviders && (
          <Tabs.Tab
            label="OIDC Providers"
            isActive={activeTab === 'oidcProviders'}
            onSelect={() => setActiveTab('oidcProviders')}
            badgeCount={team.teamOIDCProvidersCount ?? 0}
          />
        )}
      </Tabs>
      <SettingsPageTableActions>
        <SettingsPageTableActions.TooltipButton
          id="add-team-members-button"
          type="secondary"
          size="large"
          showTooltip={!canAdd}
          tooltip={
            activeTab === 'oidcProviders'
              ? 'You are not permitted to add an OIDC provider.'
              : `You are not permitted to add ${activeTab} to this team.`
          }
          onClick={() => setModalToShow(activeTab)}
          isDisabled={!canAdd}
        >
          {buttonLabel}
        </SettingsPageTableActions.TooltipButton>
        <SettingsPageTableActions.SearchInput
          label={searchPlaceholder}
          placeholder={searchPlaceholder}
          value={searchValue}
          onChange={setSearchValue}
          size="medium"
        />
      </SettingsPageTableActions>
      <EditTeamModal
        isOpen={isEditing}
        onHide={() => setIsEditing(false)}
        team={team as UpdateTeamRequest}
      />
      {activeTab === 'members' && (
        <TeamMembersTable
          team={team}
          search={debouncedSearch}
          isShowingAddMembersModal={modalToShow === 'members'}
          isTeamRoleIDPManaged={isTeamRoleIDPManaged}
          isTeamMembershipIDPManaged={isTeamMembershipIDPManaged}
          onHideAddMembersModal={() => setModalToShow(null)}
        />
      )}
      {(activeTab === 'projects' || activeTab === 'releaseGroups') && (
        <TeamProjects
          team={team}
          search={debouncedSearch}
          isShowingAddProjectsModal={modalToShow === 'projects' || modalToShow === 'releaseGroups'}
          onHideAddProjectsModal={() => setModalToShow(null)}
          mode={activeTab}
        />
      )}
      {activeTab === 'oidcProviders' && (
        <AccessGate behavior="showPage">
          <OIDCProvidersTable
            filter={oidcFilter}
            searchValue={searchValue}
            currentTeamId={team.id}
          />
        </AccessGate>
      )}
      <ModalCreateOIDCProvider
        isOpen={modalToShow === 'oidcProviders'}
        onHide={() => setModalToShow(null)}
        teamId={team.id}
      />
    </div>
  );
};

export default TeamSettings;
